/**
 * myDayCloud.js — Firestore sync for My Day.
 *
 * Everything the owner plans lives in ONE owner-scoped document,
 * myDayData/{uid}, the same shape the Expense Calculator uses: a single
 * real-time listener and one debounced write, which keeps the page instant
 * offline (Firestore's persistent cache) and trivially consistent across the
 * owner's phone and laptop.
 *
 * Completed work older than KEEP_DONE_DAYS is pruned on save so the document
 * stays far below Firestore's 1 MB ceiling — history the Insights tab needs
 * (14 days) is always well inside that window.
 */
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { myDayPayload } from './myDayUtils';

// The payload shape and pruning live in myDayUtils (Firebase-free) so the page
// can fingerprint what it would write; re-exported here for existing callers.
export { KEEP_DONE_DAYS, pruneTasks } from './myDayUtils';

const ref = uid => doc(db, 'myDayData', uid);

/**
 * Subscribe to the owner's planner doc. Echoes of our own optimistic writes
 * are ignored so the cursor never jumps while typing.
 * Calls onData(data | null, { exists, fromCache }). Returns an unsubscribe fn.
 */
export function subscribeMyDay(uid, onData, onError) {
  return onSnapshot(
    ref(uid),
    snap => {
      if (snap.metadata.hasPendingWrites) return;
      onData(snap.exists() ? snap.data() : null, {
        exists: snap.exists(),
        fromCache: snap.metadata.fromCache,
      });
    },
    err => { if (onError) onError(err); }
  );
}

/** Write the whole planner state. */
export function saveMyDay(uid, state) {
  return setDoc(ref(uid), {
    ...myDayPayload(state),
    updatedAt: serverTimestamp(),
    updatedAtMs: Date.now(),
  });
}
